import type React from "react";
import {useRef, useState} from "react";
import {Box, Typography} from "@mui/material";
import AttachIcon from "~/icons/svg/AttachIcon.svg?react";

type EvidenceDropZoneProps = {
    file: File | null;
    onFile: (file: File) => void;
    disabled?: boolean;
};

/**
 * The evidence modal's drop area (`.sxc-drop` in `sup-compliance.jsx`): one box that takes a file
 * either dropped on it or picked from the dialog a click (or Enter / Space) opens. Single file —
 * a multi-file drop keeps the first.
 */
export const EvidenceDropZone: React.FC<EvidenceDropZoneProps> = ({file, onFile, disabled = false}) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [isDragOver, setIsDragOver] = useState(false);

    const openPicker = () => {
        if (!disabled) inputRef.current?.click();
    };

    const pick = (event: React.ChangeEvent<HTMLInputElement>) => {
        const picked = event.target.files?.[0];
        // Clear the input so picking the same file again still fires `change`.
        event.target.value = '';
        if (picked) onFile(picked);
    };

    const dragOver = (event: React.DragEvent) => {
        // Without preventDefault the browser refuses the drop and opens the file instead.
        event.preventDefault();
        if (!disabled) setIsDragOver(true);
    };

    const drop = (event: React.DragEvent) => {
        event.preventDefault();
        setIsDragOver(false);
        const dropped = event.dataTransfer.files[0];
        if (!disabled && dropped) onFile(dropped);
    };

    const keyDown = (event: React.KeyboardEvent) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        openPicker();
    };

    const isActive = isDragOver && !disabled;

    return (
        <Box
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-disabled={disabled}
            aria-label={file ? `Attached ${file.name}. Choose another file` : 'Drop a file here or click to attach'}
            onClick={openPicker}
            onKeyDown={keyDown}
            onDragOver={dragOver}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={drop}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                height: 96,
                px: 2,
                border: '1.5px dashed',
                borderColor: isActive ? 'primary.main' : 'divider',
                borderRadius: '12px',
                bgcolor: 'action.hover',
                color: isActive ? 'primary.main' : 'text.secondary',
                cursor: disabled ? 'default' : 'pointer',
                opacity: disabled ? 0.6 : 1,
                outline: 'none',
                transition: 'border-color 120ms, color 120ms',
                '&:hover, &:focus-visible': disabled ? undefined : {borderColor: 'primary.main', color: 'primary.main'},
            }}
        >
            <AttachIcon width={18} height={18}/>
            <Typography variant="body2" color="inherit" noWrap>
                {file ? file.name : 'Drop a file here or click to attach'}
            </Typography>
            <input ref={inputRef} type="file" hidden onChange={pick}/>
        </Box>
    );
};
